import {
  MpvValue,
  MpvOption,
  ContentfulCloudinaryImageComponent,
  SiteflowResponse,
} from "../types/ContentfulTypes";
import { AttributeConstraint } from "../types/ConstraintsTypes";
import { CloudinaryVideoMedia } from "../types/CloudinaryTypes";
import { CtaOverrideEntries } from "../build/product-builder-ctas/ctaOverrideTypes";
import { bulkOrderBigQuantity } from "./bulkOrder";
import RangeType from "../types/RangeTypes";
import { isInRanges } from "./isInRanges";
import { ProductBuilderSelection } from "../types/ProductBuilderTypes";

export const determineOptionsToShow = (
  options: MpvValue[],
  compatibleValues: string[],
  showIncompatibleValues: boolean
): string[] => {
  if (showIncompatibleValues) {
    return (options || []).map(o => o.key);
  }

  const compatibleValuesToReturn = [];
  for (const option of options) {
    if (compatibleValues.includes(option.key)) {
      compatibleValuesToReturn.push(option.key);
    }
  }

  const orderedCompatibleValues = [...new Set([...compatibleValuesToReturn, ...compatibleValues])];

  return orderedCompatibleValues;
};

export const isValueCompatible = (
  option: MpvOption,
  value: MpvValue,
  compatibleValues: Record<string, string[]>
): boolean => {
  const mappedValue = { [option.key]: value.key };

  let compatible = true;
  Object.entries(mappedValue).map(([o, v]: [string, string]) => {
    if (!compatibleValues[`${o}`] || !compatibleValues[`${o}`].includes(v)) {
      compatible = false;
    }
  });

  return compatible;
};

export const isValueConstrained = (data: {
  option: string;
  value: string;
  selections: Record<string, string>;
  attributeConstraints: AttributeConstraint[];
}): {
  isConstrained: boolean;
  isAlwaysConstrained: boolean;
} => {
  const { option, value, attributeConstraints, selections } = data;

  let constrained = false;
  let alwaysConstrained = false;
  // Check if attribute combination is constrained
  const constrainedList = attributeConstraints?.filter(constraint => {
    // Option is on constraint
    if (isOnConstraint(value, constraint[`${option}`])) {
      let constrained = true;

      // If the attribute is constrained for the entire product, always constrain/disable it
      if (Object.keys(constraint).length === 1) {
        alwaysConstrained = true;
      }

      Object.keys(constraint).forEach(key => {
        // If other selection is not constrained, this option is not constrained
        if (typeof constraint[`${key}`][0] === "string") {
          const valueConstraint = constraint[`${key}`] as string[];
          if (key !== option && !valueConstraint.includes(selections[`${key}`])) {
            constrained = false;
          }
        } else {
          const rangeConstraint = constraint[`${key}`] as RangeType[];
          if (
            key !== option &&
            (!selections[`${key}`] || !isInRanges(selections[`${key}`], rangeConstraint))
          ) {
            constrained = false;
          }
        }
      });
      return constrained;
    }
    return false;
  });
  if (constrainedList && constrainedList.length > 0) constrained = true;

  return {
    isConstrained: constrained,
    isAlwaysConstrained: alwaysConstrained,
  };
};

const isOnConstraint = (value: string, constraint: string[] | RangeType[]) => {
  if (!constraint || constraint.length === 0 || !value) {
    return false;
  }

  if (typeof constraint[0] === "string") {
    const valueConstraint = constraint as string[];
    return valueConstraint.includes(value);
  } else {
    const rangeConstraint = constraint as RangeType[];
    return isInRanges(value, rangeConstraint);
  }
};

export const onPageBuilderOptionValueClickHandler = (props: {
  option: MpvOption;
  selectedValue: string | null;
  attributeConstraints: AttributeConstraint[];
  selections: Record<string, string>;
  compatibleValues: Record<string, string[]>;
  userSelectValue: (selections: Record<string, string | null | undefined>) => void;
  userSelectIncompatibleValue: (selections: Record<string, string | null | undefined>) => void;
  isRanged?: boolean;
}) => {
  const {
    option,
    selectedValue,
    attributeConstraints,
    selections,
    compatibleValues,
    isRanged = false,
    userSelectValue,
    userSelectIncompatibleValue,
  } = props;

  if (isRanged) {
    userSelectValue({ [option.key]: selectedValue });
  } else {
    const selectedMpvValue = option?.options?.find(v => v.key === selectedValue);

    if (selectedMpvValue) {
      const { isConstrained } = isValueConstrained({
        option: option.key,
        value: selectedValue || "",
        selections,
        attributeConstraints,
      });
      const isCompatible = isValueCompatible(option, selectedMpvValue, compatibleValues);

      if (isCompatible && !isConstrained) {
        userSelectValue({ [option.key]: selectedValue });
      } else {
        userSelectIncompatibleValue({ [option.key]: selectedValue });
      }
    }
  }
};

export const determineValueStatus = ({
  isPrimaryAttribute,
  isCompatible,
  isConstrained,
  isAlwaysConstrained,
  outOfStockMessage,
  incompatibleMessage,
}: {
  isPrimaryAttribute: boolean;
  isCompatible: boolean;
  isConstrained: boolean;
  isAlwaysConstrained: boolean;
  outOfStockMessage: string;
  incompatibleMessage: string;
}): {
  selectorDisplayed: boolean;
  selectorDisabled: boolean;
  selectorStatusMsg: string;
} => {
  // By default, we display and enable the button with no message
  let selectorDisplayed = true;
  let selectorDisabled = false;
  let selectorStatusMsg = "";
  // If this attribute is primary attribute
  if (isPrimaryAttribute) {
    if (isAlwaysConstrained) {
      // If the value is constrained on the entire product, disable button with OOS message
      selectorDisplayed = true;
      selectorDisabled = true;
      selectorStatusMsg = outOfStockMessage;
    } else if (isConstrained && isCompatible) {
      // If this value is constrained but compatible, show a enabled button with OOS message
      selectorDisplayed = true;
      selectorDisabled = false;
      selectorStatusMsg = outOfStockMessage;
    } else if (!isCompatible && !isConstrained) {
      // If this value is incompatible but not constained, show an enabled button with incompatibilities message
      selectorDisplayed = true;
      selectorDisabled = false;
      selectorStatusMsg = incompatibleMessage;
    } else if (isConstrained && !isCompatible) {
      // If this value is incompatible and constrained, show as constrained - enabled button with OOS message.
      selectorDisplayed = true;
      selectorDisabled = false;
      selectorStatusMsg = outOfStockMessage;
    }
  } else {
    // If attribute is non-primary attribute
    // If this value is constrained but compatible, show a disabled button with OOS message
    if (isConstrained && isCompatible) {
      selectorDisplayed = true;
      selectorDisabled = true;
      selectorStatusMsg = outOfStockMessage;
    } else if (!isConstrained && !isCompatible) {
      // If this value is incompatible but not contrained, hide it
      selectorDisplayed = false;
    } else if (isConstrained && !isCompatible) {
      // If this value is incompatible and constrained, show as incompatible - hide it
      selectorDisplayed = false;
    }
  }

  return {
    selectorDisplayed,
    selectorDisabled,
    selectorStatusMsg,
  };
};

export const hasConstrainedOptionValues = (
  selections: Record<string, string>,
  attributeConstraints: AttributeConstraint[]
) => {
  const hasConstrainedOptionValues = Object.entries(selections).some(
    ([option, value]: [string, string]) => {
      const { isConstrained } = isValueConstrained({
        option,
        value,
        selections,
        attributeConstraints,
      });

      return isConstrained;
    }
  );
  return hasConstrainedOptionValues;
};

export const getDefaultImage = (
  heroGallery: (ContentfulCloudinaryImageComponent | CloudinaryVideoMedia)[]
) => {
  const media = heroGallery.find(galleryMedia => getMediaImage(galleryMedia));
  return getMediaImage(media);
};

export const getMediaImage = (
  media: CloudinaryVideoMedia | ContentfulCloudinaryImageComponent | undefined
) => {
  const videoMedia = media as CloudinaryVideoMedia;
  const imageMedia = media as ContentfulCloudinaryImageComponent;

  if (videoMedia?.video) {
    const videoUrl = constructVideoUrl(videoMedia);
    return `${videoUrl}.jpg`;
  }
  if (imageMedia?.cloudinaryImage?.cloudinaryUrl) {
    return imageMedia.cloudinaryImage.cloudinaryUrl;
  }

  return "";
};

export const constructVideoUrl = (video: CloudinaryVideoMedia) => {
  const transformations =
    "c_scale,dpr_auto,f_auto,q_auto,t_productPageHeroGalleryTransformation,w_auto,e_volume:mute";
  return `${video.video.deliveryUrl}/video/upload/${transformations}/${video.video.publicId}`;
};

export const showTextBoxSelector = (
  compatibleRanges: RangeType[],
  clientSideStateInitialized: boolean
) => {
  if (!compatibleRanges || compatibleRanges.length === 0 || !clientSideStateInitialized) {
    return false;
  }

  const singleOptions = compatibleRanges.filter(range => range.minimum === range.maximum);
  if (singleOptions.length <= 2 && singleOptions.length === compatibleRanges.length) {
    // We should only show the selector if there are more than 2 possible values a user can input
    return false;
  }

  return true;
};

export const isOptionVisible = (option: ProductBuilderSelection | undefined) => {
  if (
    !option?.property?.name ||
    (option?.selectionStyle !== "Text box" &&
      (option?.property?.options || []).find(value => !value.name))
  ) {
    return false;
  }
  return true;
};
